import { parseContractComplianceError } from 'api/requests/contract-compliance-requests';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__CONCURRENT_CHANGE,
  CONTRACT_COMPLIANCE__DEFAULT_SAVED,
  CONTRACT_COMPLIANCE__NOT_FOUND,
  CONTRACT_COMPLIANCE__PANEL_LOAD_ERROR,
  GENERAL__ERROR_MESSAGE,
} from 'translations/constants';
import { IJustificationPayload } from 'types/contract-compliance/IContractCompliance';
import { getContractComplianceErrorMessage } from '../utils';
import { PanelCallbacks, PanelStatus } from './types';
import { useJustification, UseJustificationResult } from './useJustification';

export interface RuleDefaultAdapter<
  TRule extends string,
  TValue extends { rowVersion: string },
  TDraft extends Record<string, string>,
  TBody,
> {
  emptyDraft: TDraft;
  load: (rule: TRule) => Promise<TValue>;
  save: (rule: TRule, body: TBody) => Promise<unknown>;
  toDraft: (value: TValue) => TDraft;
  validate: (rule: TRule, draft: TDraft) => Partial<Record<keyof TDraft, string>>;
  isComplete: (rule: TRule, draft: TDraft) => boolean;
  toBody: (rule: TRule, draft: TDraft, rowVersion: string, justification: IJustificationPayload) => TBody;
}

export interface UseRuleDefaultPanelResult<TRule extends string, TDraft extends Record<string, string>> {
  rule: TRule | null;
  status: PanelStatus;
  draft: TDraft;
  setField: (field: keyof TDraft, value: string) => void;
  fieldErrors: Partial<Record<keyof TDraft, string>>;
  isComplete: boolean;
  isSaving: boolean;
  isConfirmOpen: boolean;
  loadErrorMessage: string | null;
  saveErrorMessage: string | null;
  justification: UseJustificationResult;
  open: (rule: TRule) => void;
  retryLoad: () => void;
  requestSave: () => void;
  cancelConfirm: () => void;
  confirmSave: () => Promise<void>;
  close: () => void;
}

export const useRuleDefaultPanel = <
  TRule extends string,
  TValue extends { rowVersion: string },
  TDraft extends Record<string, string>,
  TBody,
>(
  adapter: RuleDefaultAdapter<TRule, TValue, TDraft, TBody>,
  callbacks: PanelCallbacks,
): UseRuleDefaultPanelResult<TRule, TDraft> => {
  const [t] = useTranslation();
  const justification = useJustification();
  const [rule, setRule] = useState<TRule | null>(null);
  const [status, setStatus] = useState<PanelStatus>('closed');
  const [loaded, setLoaded] = useState<TValue | null>(null);
  const [draft, setDraft] = useState<TDraft>(adapter.emptyDraft);
  const [showErrors, setShowErrors] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  // Refs, not state: the double-submit guard and the stale-response guard must see the new value before React
  // re-renders (same as the feature access panel).
  const savingRef = useRef(false);
  const activeRuleRef = useRef<TRule | null>(null);
  // Bumped on every open and close: reopening the same rule must not receive the previous session's answers.
  const sessionRef = useRef(0);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  const { reset: resetJustification, toPayload } = justification;

  // A save error stays until the user's first edit after it (reloads after a conflict do not count).
  const { text: justificationText, document: justificationDocument } = justification;
  useEffect(() => {
    setSaveErrorMessage(null);
  }, [justificationText, justificationDocument]);

  const load = useCallback(
    async (target: TRule) => {
      const session = sessionRef.current;
      setStatus('loading');
      setLoadErrorMessage(null);
      try {
        const value = await adapter.load(target);
        if (session !== sessionRef.current) return;
        setLoaded(value);
        setDraft(adapter.toDraft(value));
        setStatus('ready');
      } catch (error) {
        if (session !== sessionRef.current) return;
        setLoadErrorMessage(
          getContractComplianceErrorMessage(
            t,
            parseContractComplianceError(error),
            CONTRACT_COMPLIANCE__PANEL_LOAD_ERROR,
          ),
        );
        setStatus('loadError');
      }
    },
    [adapter, t],
  );

  const reset = useCallback(() => {
    sessionRef.current += 1;
    activeRuleRef.current = null;
    setRule(null);
    setStatus('closed');
    setLoaded(null);
    setDraft(adapter.emptyDraft);
    setShowErrors(false);
    setIsConfirmOpen(false);
    setLoadErrorMessage(null);
    setSaveErrorMessage(null);
    resetJustification();
  }, [adapter, resetJustification]);

  const open = useCallback(
    (target: TRule) => {
      reset();
      activeRuleRef.current = target;
      setRule(target);
      load(target);
    },
    [load, reset],
  );

  const retryLoad = useCallback(() => {
    if (activeRuleRef.current !== null) load(activeRuleRef.current);
  }, [load]);

  const setField = useCallback((field: keyof TDraft, value: string) => {
    setDraft(previous => ({ ...previous, [field]: value }));
    setSaveErrorMessage(null);
  }, []);

  const validationErrors = useMemo(() => (rule ? adapter.validate(rule, draft) : {}), [adapter, rule, draft]);
  // Only shown after the first save attempt, so fields don't turn red while the user is still typing.
  const fieldErrors = showErrors ? validationErrors : {};

  const isComplete = rule !== null && adapter.isComplete(rule, draft);

  const requestSave = useCallback(() => {
    if (!isComplete) return;
    setShowErrors(true);
    if (status !== 'ready' || savingRef.current || justification.isUploading) return;
    if (Object.keys(validationErrors).length > 0) return;
    setIsConfirmOpen(true);
  }, [isComplete, status, validationErrors, justification.isUploading]);

  /** Back to the panel with everything the user entered still there. */
  const cancelConfirm = useCallback(() => setIsConfirmOpen(false), []);

  const confirmSave = useCallback(async () => {
    const target = activeRuleRef.current;
    if (savingRef.current || target === null || !loaded) return;
    savingRef.current = true;
    setIsSaving(true);
    setSaveErrorMessage(null);
    const session = sessionRef.current;
    const stillShowing = () => sessionRef.current === session;
    try {
      await adapter.save(target, adapter.toBody(target, draft, loaded.rowVersion, toPayload()));
      if (stillShowing()) reset();
      callbacksRef.current.onSaved(CONTRACT_COMPLIANCE__DEFAULT_SAVED);
    } catch (error) {
      const parsed = parseContractComplianceError(error);
      const message = getContractComplianceErrorMessage(t, parsed, GENERAL__ERROR_MESSAGE);
      if (stillShowing()) setIsConfirmOpen(false);
      if (parsed.key === CONTRACT_COMPLIANCE__NOT_FOUND) {
        if (stillShowing()) reset();
        callbacksRef.current.onGone(message);
      } else if (!stillShowing()) {
        if (parsed.key === CONTRACT_COMPLIANCE__CONCURRENT_CHANGE) callbacksRef.current.onStale();
        callbacksRef.current.onError(message);
      } else if (parsed.key === CONTRACT_COMPLIANCE__CONCURRENT_CHANGE) {
        // Reload so the user sees the other change and the next save carries the fresh rowVersion.
        setSaveErrorMessage(message);
        callbacksRef.current.onStale();
        load(target);
      } else {
        // Validation errors, 5xx and timeouts: the panel stays open with the entered values and shows the error at
        // its top. Not the bottom-right banner: in a side panel it covers the Save button.
        setSaveErrorMessage(message);
      }
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [adapter, draft, loaded, toPayload, reset, load, t]);

  const close = useCallback(() => {
    reset();
    callbacksRef.current.onClosed();
  }, [reset]);

  return {
    rule,
    status,
    draft,
    setField,
    fieldErrors,
    isComplete,
    isSaving,
    isConfirmOpen,
    loadErrorMessage,
    saveErrorMessage,
    justification,
    open,
    retryLoad,
    requestSave,
    cancelConfirm,
    confirmSave,
    close,
  };
};
